/**
 * CR412 Phase 1.3A / CR413: Deal Card View Tab
 * 
 * Read-only view of the currently published deal card config.
 * CR413: Environment selector, promote scaffold, environment-aware metadata.
 */

import { useState, useCallback, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Eye,
  RefreshCw,
  Settings,
  Puzzle,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Upload,
  ArrowUpRight,
} from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { toast } from "sonner";
import type { PublishedDealCardConfig, DealCardPublishedConfigJson } from "@/hooks/usePublishedDealCardConfig";
import { usePromoteDealCardConfig, type PublishEnvironment } from "@/hooks/usePromoteDealCardConfig";
import { useDealSourceMode } from "@/hooks/useDealSourceMode";
import { DealSourcePicker } from "@/components/admin/deal-cards/DealSourcePicker";
import { PreviewWorkbench } from "@/components/admin/deal-cards";
import { EnvironmentSelector } from "@/components/admin/deal-cards/EnvironmentSelector";
import { PromoteConfigModal } from "@/components/admin/deal-cards/PromoteConfigModal";
import { DEFAULT_FACES_CONFIG, type CardFacesConfig } from "@/lib/deals/cardFaces";

// ================================================================
// CANONICAL DEAL TYPES
// ================================================================

const CANONICAL_DEAL_TYPES = ["BONUS", "DISCOUNT", "POINTS", "IN_KIND", "UNCLASSIFIED"];

// ================================================================
// TYPES
// ================================================================

interface DealCardViewTabProps {
  onNavigateToDataElements?: () => void;
  onNavigateToComponents?: () => void;
  selectedType?: string;
  onSelectedTypeChange?: (type: string) => void;
  /** CR413: Current environment from parent */
  selectedEnvironment?: PublishEnvironment;
  /** CR413: Callback when environment changes */
  onSelectedEnvironmentChange?: (env: PublishEnvironment) => void;
  published?: PublishedDealCardConfig | null;
  publishedLoading?: boolean;
  publishedError?: string | null;
  onRefreshPublished?: () => void;
  onSeedPublish?: () => Promise<void>;
}

// ================================================================
// HELPERS
// ================================================================

function shortId(uuid: string): string {
  return uuid.slice(0, 8);
}

function getConfigSummary(config: DealCardPublishedConfigJson | undefined): {
  dataElementKeys: string[];
  componentKeys: string[];
  schemaVersion: number | null;
} {
  return {
    dataElementKeys: config?.data_elements ? Object.keys(config.data_elements) : [],
    componentKeys: config?.components ? Object.keys(config.components) : [],
    schemaVersion: config?.meta?.schemaVersion ?? null,
  };
}

// ================================================================
// COMPONENT
// ================================================================

export function DealCardViewTab({
  onNavigateToDataElements,
  onNavigateToComponents,
  selectedType: externalType,
  onSelectedTypeChange,
  selectedEnvironment: externalEnv,
  onSelectedEnvironmentChange,
  published,
  publishedLoading,
  publishedError,
  onRefreshPublished,
  onSeedPublish,
}: DealCardViewTabProps) {
  const [internalType, setInternalType] = useState<string>("BONUS");
  const selectedType = externalType ?? internalType;
  const setSelectedType = onSelectedTypeChange ?? setInternalType;
  
  const [internalEnv, setInternalEnv] = useState<PublishEnvironment>("staging");
  const selectedEnvironment = externalEnv ?? internalEnv;
  const setSelectedEnvironment = onSelectedEnvironmentChange ?? setInternalEnv;
  
  const [facesConfig, setFacesConfig] = useState<CardFacesConfig>(DEFAULT_FACES_CONFIG);
  const [seeding, setSeeding] = useState(false);
  const [promoteOpen, setPromoteOpen] = useState(false);

  const loading = publishedLoading ?? false;
  const error = publishedError ?? null;

  // CR413/CR415: Promote hook
  const { promote, loading: promoting, error: promoteError, clearError: clearPromoteError } = usePromoteDealCardConfig();

  // CR426: Deal source mode (Sample ↔ Live)
  const dealSource = useDealSourceMode(selectedEnvironment, selectedType);
  const { deal: ssotDeal, meta: ssotMeta, loading: ssotLoading, error: ssotError } = dealSource;

  // Build preview overrides from published config
  // CR414: Components normalization happens inside bridgeTemplateToV2Spec
  const templateOverride = useMemo(() => {
    if (!published?.config_json) return undefined;
    const cfg = published.config_json;
    const de = (cfg.data_elements ?? {}) as Record<string, unknown>;
    const comp = (cfg.components ?? {}) as Record<string, unknown>;
    return {
      sections: de.sections as Record<string, boolean> | undefined,
      labels: de.labels as Record<string, string> | undefined,
      header: de.header as Record<string, unknown> | undefined,
      extended_sections: de.extended_sections as Record<string, boolean> | undefined,
      styling: de.styling as Record<string, unknown> | undefined,
      section_order: de.section_order as string[] | undefined,
      theme_preset: de.theme_preset as string | undefined,
      surfaceOverrides: de.surfaceOverrides as Record<string, unknown> | undefined,
      components: comp,
    };
  }, [published]);

  // CR414: Debug diagnostics for components normalization + starburst toggle
  const cr414Diagnostics = useMemo(() => {
    if (!published?.config_json) return null;
    const cfg = published.config_json;
    const comp = (cfg.components ?? {}) as Record<string, unknown>;
    const de = (cfg.data_elements ?? {}) as Record<string, unknown>;
    const hasNestedComponents = !!(comp.components && typeof comp.components === 'object');
    const effectiveComponentMap = hasNestedComponents
      ? { ...comp, ...(comp.components as Record<string, unknown>) }
      : comp;
    const { components: _rm, ...flatMap } = effectiveComponentMap as Record<string, unknown> & { components?: unknown };
    
    const surfOv = de.surfaceOverrides as Record<string, unknown> | undefined;
    const primaryOv = surfOv?.primary as Record<string, unknown> | undefined;
    const blockToggles = primaryOv?.blockToggles as Record<string, boolean> | undefined;
    const starburstToggle = blockToggles?.starburst;
    const ribbonConfig = flatMap.ribbon as Record<string, unknown> | undefined;

    return {
      componentsShape: hasNestedComponents ? 'nested (normalized)' : 'flat',
      componentKeys: Object.keys(flatMap),
      starburstToggle: starburstToggle ?? 'not set (default)',
      starburstTogglePath: blockToggles?.starburst !== undefined
        ? 'data_elements.surfaceOverrides.primary.blockToggles.starburst'
        : 'legacy fallback',
      ribbonColorSource: ribbonConfig?.color_source ?? 'not set',
      ribbonColorHex: ribbonConfig?.color_hex ?? 'none',
    };
  }, [published]);

  const summary = useMemo(() => getConfigSummary(published?.config_json), [published]);

  const handleSeedPublish = useCallback(async () => {
    if (!onSeedPublish) return;
    setSeeding(true);
    try {
      await onSeedPublish();
    } finally {
      setSeeding(false);
    }
  }, [onSeedPublish]);

  const handlePromote = useCallback(async (targetEnv: PublishEnvironment) => {
    clearPromoteError();
    const result = await promote(selectedEnvironment, targetEnv, selectedType);
    if (result) {
      toast.success(`Promoted ${selectedType}: ${selectedEnvironment} → ${targetEnv}`);
      setPromoteOpen(false);
      // Refresh to show updated state
      onRefreshPublished?.();
    } else {
      // Error is set in the hook — conflict detection handled by modal
      toast.error("Promotion failed — see details in modal");
    }
  }, [promote, clearPromoteError, selectedEnvironment, selectedType, onRefreshPublished]);

  return (
    <div className="space-y-4">
      {/* TOP BAR */}
      <div className="flex items-center justify-between border-b pb-3">
        <div className="flex items-center gap-3">
          <Label className="text-sm font-medium text-muted-foreground">Deal Type:</Label>
          <Select value={selectedType} onValueChange={setSelectedType}>
            <SelectTrigger className="w-[180px] h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CANONICAL_DEAL_TYPES.map((type) => (
                <SelectItem key={type} value={type} className="text-sm">
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Separator orientation="vertical" className="h-6" />

          {/* CR413: Environment selector */}
          <EnvironmentSelector
            value={selectedEnvironment}
            onChange={setSelectedEnvironment}
          />
        </div>

        <div className="flex items-center gap-2">
          {/* CR415: Promote button (visible when config exists and env has targets) */}
          {published && selectedEnvironment !== "production" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPromoteOpen(true)}
              className="gap-1"
            >
              <ArrowUpRight className="h-3.5 w-3.5" />
              Promote…
            </Button>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={onRefreshPublished}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* PUBLISHED METADATA */}
      {published && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground px-1">
          <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
          <span className="font-medium text-foreground">Published</span>
          <span>•</span>
          <span className="font-mono">v{shortId(published.active_version_id)}</span>
          <span>•</span>
          <Badge variant="secondary" className="text-[10px] h-4">{selectedEnvironment}</Badge>
          <span>•</span>
          <span>
            <Clock className="h-3 w-3 inline mr-0.5" />
            {formatDistanceToNow(new Date(published.created_at), { addSuffix: true })}
          </span>
          <span className="text-[10px] font-mono opacity-60">
            ({format(new Date(published.created_at), "yyyy-MM-dd HH:mm")})
          </span>
        </div>
      )}

      {!published && !loading && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground px-1">
          <AlertTriangle className="h-3.5 w-3.5 text-warning" />
          <span>No published config for <strong>{selectedType}</strong> in <strong>{selectedEnvironment}</strong> yet.</span>
          {onSeedPublish && selectedEnvironment === "staging" && (
            <Button
              variant="outline"
              size="sm"
              className="ml-2 h-6 text-xs"
              onClick={handleSeedPublish}
              disabled={seeding}
            >
              <Upload className="h-3 w-3 mr-1" />
              {seeding ? "Publishing…" : "Publish Current Config to Staging"}
            </Button>
          )}
        </div>
      )}

      {error && (
        <div className="text-xs text-destructive px-1">
          Error: {error}
        </div>
      )}

      {/* LOADING */}
      {loading && (
        <div className="space-y-3">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      )}

      {/* MAIN CONTENT */}
      {!loading && (
        <div className="grid grid-cols-12 gap-4">
          {/* LEFT: Config Summaries */}
          <div className="col-span-4 space-y-4">
            {/* Data Elements Summary */}
            <Card>
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Settings className="h-4 w-4 text-muted-foreground" />
                  Data Elements
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 pt-0">
                {published?.config_json?.data_elements ? (
                  <ScrollArea className="h-[200px]">
                    <div className="space-y-2">
                      {summary.dataElementKeys.map((key) => {
                        const val = (published.config_json.data_elements as Record<string, unknown>)?.[key];
                        const isEnabled = val === true || (typeof val === "object" && val && (val as Record<string, unknown>).enabled !== false);
                        return (
                          <div key={key} className="flex items-center justify-between text-xs">
                            <span className="font-mono text-muted-foreground">{key}</span>
                            <Badge variant={isEnabled ? "default" : "outline"} className="text-[10px] h-4">
                              {isEnabled ? "ON" : "OFF"}
                            </Badge>
                          </div>
                        );
                      })}
                      {summary.dataElementKeys.length === 0 && (
                        <p className="text-xs text-muted-foreground italic">No data elements configured</p>
                      )}
                    </div>
                  </ScrollArea>
                ) : (
                  <p className="text-xs text-muted-foreground italic">Not published yet</p>
                )}
                {onNavigateToDataElements && (
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto p-0 mt-3 text-xs"
                    onClick={onNavigateToDataElements}
                  >
                    <Settings className="h-3 w-3 mr-1" />
                    Open Configure Data Elements →
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Components Summary */}
            <Card>
              <CardHeader className="py-3 px-4">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Puzzle className="h-4 w-4 text-muted-foreground" />
                  Components
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 pt-0">
                {published?.config_json?.components ? (
                  <ScrollArea className="h-[200px]">
                    <div className="space-y-2">
                      {summary.componentKeys.map((key) => {
                        const val = (published.config_json.components as Record<string, unknown>)?.[key];
                        const isObj = typeof val === "object" && val;
                        return (
                          <div key={key} className="flex items-center justify-between text-xs">
                            <span className="font-mono text-muted-foreground">{key}</span>
                            {isObj ? (
                              <Badge variant="secondary" className="text-[10px] h-4">
                                {Object.keys(val as Record<string, unknown>).length} keys
                              </Badge>
                            ) : (
                              <span className="text-[10px] text-muted-foreground">{String(val)}</span>
                            )}
                          </div>
                        );
                      })}
                      {summary.componentKeys.length === 0 && (
                        <p className="text-xs text-muted-foreground italic">No components configured</p>
                      )}
                    </div>
                  </ScrollArea>
                ) : (
                  <p className="text-xs text-muted-foreground italic">Not published yet</p>
                )}
                {onNavigateToComponents && (
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto p-0 mt-3 text-xs"
                    onClick={onNavigateToComponents}
                  >
                    <Puzzle className="h-3 w-3 mr-1" />
                    Open Components →
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Schema info */}
            {published && (
              <div className="text-[10px] text-muted-foreground font-mono px-2 space-y-0.5">
                <div>version_id: {published.active_version_id}</div>
                <div>environment: {selectedEnvironment}</div>
                <div>schema: v{summary.schemaVersion ?? "?"}</div>
                {published.source_version_id && (
                  <div>source: {shortId(published.source_version_id)}</div>
                )}
              </div>
            )}
          </div>

          {/* RIGHT: Preview */}
          <div className="col-span-8 flex flex-col gap-4">
          {/* CR426: Deal Source Picker */}
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <DealSourcePicker
              mode={dealSource.mode}
              onModeChange={dealSource.setMode}
              meta={ssotMeta}
              loading={ssotLoading}
              liveDeals={dealSource.liveDeals}
              liveDealsLoading={dealSource.liveDealsLoading}
              liveSearch={dealSource.liveSearch}
              onLiveSearchChange={dealSource.setLiveSearch}
              selectedLiveDealId={dealSource.selectedLiveDealId}
              onSelectLiveDeal={dealSource.setSelectedLiveDealId}
            />
            {ssotError && (
              <Badge variant="destructive" className="text-[10px]">
                {ssotError}
              </Badge>
            )}
          </div>

            <div className="flex-1 min-h-[500px]">
              <PreviewWorkbench
                deal={ssotDeal || null}
                sampleDeal={null}
                dealType={selectedType}
                hasUnsavedChanges={false}
                previewKey={`view-${selectedEnvironment}-${selectedType}-${published?.active_version_id ?? "none"}`}
                templateOverride={templateOverride}
                facesConfig={facesConfig}
                onFacesConfigChange={setFacesConfig}
                loading={false}
                isAdmin={true}
                environment={selectedEnvironment}
                renderMeta={ssotMeta?.renderPayload ?? null}
                className="h-full"
              />
            </div>

            {/* Published truth footer */}
            <div className="px-3 py-2 text-xs text-muted-foreground bg-muted/50 rounded border border-dashed flex items-center gap-2">
              <Eye className="h-3.5 w-3.5" />
              <span>
                This preview reflects the <strong>published {selectedEnvironment} config</strong> — no drafts or overrides.
              </span>
            </div>

            {/* CR414: Debug diagnostics strip */}
            {cr414Diagnostics && (
              <div className="px-3 py-2 text-[10px] font-mono text-muted-foreground bg-muted/30 rounded border border-dashed space-y-0.5">
                <div className="font-medium text-xs text-foreground mb-1">CR414 Diagnostics</div>
                <div>components shape: <span className="text-foreground">{cr414Diagnostics.componentsShape}</span></div>
                <div>component keys: <span className="text-foreground">{cr414Diagnostics.componentKeys.join(', ') || 'none'}</span></div>
                <div>starburst toggle: <span className="text-foreground">{String(cr414Diagnostics.starburstToggle)}</span> (via {cr414Diagnostics.starburstTogglePath})</div>
                <div>ribbon color_source: <span className="text-foreground">{String(cr414Diagnostics.ribbonColorSource)}</span></div>
                <div>ribbon color_hex: <span className="text-foreground">{String(cr414Diagnostics.ribbonColorHex)}</span></div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CR415: Promote modal */}
      {published && (
        <PromoteConfigModal
          open={promoteOpen}
          onOpenChange={setPromoteOpen}
          dealType={selectedType}
          sourceEnvironment={selectedEnvironment}
          sourceVersionId={published.active_version_id}
          sourceTimestamp={published.created_at}
          onPromote={handlePromote}
          promoting={promoting}
          conflictError={promoteError === "CONFLICT"}
        />
      )}
    </div>
  );
}
